import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import PropTypes from 'prop-types';
import { BarChart, Label, Tooltip, Legend, CartesianGrid, XAxis, YAxis, Bar, ResponsiveContainer } from 'recharts';

import { emptyArray } from 'ui-components/default-props/empty';
import { getInterval } from '../../helper';
import style from './style.module.less';
import messages from './messages';
import Colors from '../utilities/colors';
import { MAIN_GRAPH_HEIGHT, Y_AXIS_TICK_LENGTH } from '../utilities/constants';
import NullStateOverlay from '../../null-state-overlay';

import CustomTooltip from '../custom-tooltip';
import CustomTooltipCustomerPerDay from '../custom-tooltip/custom-tooltip-customer-per-day';
import { useUserDateFormat } from '../../../../../hooks/use-datetime-formats';

const CustomersPerDayGraph = ({ data = emptyArray, shouldShowNullStates = false }) => {
  const intl = useIntl();
  const { formatUserDate } = useUserDateFormat();

  const messageKey = useMemo(
    () => ({
      returningCount: intl.formatMessage(messages.returningCustomers),
      newCount: intl.formatMessage(messages.newCustomers),
    }),
    [intl],
  );

  const formattedData = useMemo(() => {
    return data.map((datum) => ({
      ...datum,
    }));
  }, [data]);

  const interval = useMemo(() => getInterval(formattedData), [formattedData]);

  const formatXTicks = useCallback(
    (date) => {
      return formatUserDate(date, false);
    },
    [formatUserDate],
  );

  const formatLegend = useCallback(
    (value) => {
      return <span className={style['legend-label']}>{messageKey[value]}</span>;
    },
    [messageKey],
  );

  return (
    <>
      {shouldShowNullStates && <NullStateOverlay />}
      <ResponsiveContainer height={MAIN_GRAPH_HEIGHT} width='100%'>
        <BarChart
          data={formattedData}
          style={{
            fontSize: 12,
          }}
        >
          <CartesianGrid stroke={Colors.GREY_HEX_VALUE} />
          <XAxis dataKey='interval' interval={interval} tickFormatter={formatXTicks} tickLine={false} />
          <YAxis tickLine={false} tickSize={Y_AXIS_TICK_LENGTH}>
            <Label
              angle={-90}
              position='insideLeft'
              style={{
                fontSize: 24,
                textAnchor: 'middle',
              }}
              value={intl.formatMessage(messages.yAxisLabel)}
            />
          </YAxis>
          <Legend
            formatter={formatLegend}
            style={{ width: 80 }}
            width='100%'
            wrapperStyle={{
              display: 'flex',
              justifyContent: 'center',
            }}
          />
          <Tooltip
            content={
              <CustomTooltip>
                <CustomTooltipCustomerPerDay />
              </CustomTooltip>
            }
          />
          <Bar dataKey='newCount' fill={Colors.BAR_COLOR_PURPLE} stackId='a' />
          <Bar dataKey='returningCount' fill={Colors.BAR_COLOR_BLUE} stackId='a' />
        </BarChart>
      </ResponsiveContainer>
    </>
  );
};

CustomersPerDayGraph.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      interval: PropTypes.string,
      newCount: PropTypes.number,
      returningCount: PropTypes.number,
    }),
  ),
  shouldShowNullStates: PropTypes.bool,
};

export default CustomersPerDayGraph;
